import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";

interface LazyVisualizationProps {
  children: React.ReactNode;
  placeholder?: React.ReactNode;
  rootMargin?: string;
  threshold?: number;
  onVisibilityChange?: (isVisible: boolean) => void;
}

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
`;

const Placeholder = styled.div`
  width: 100%;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--bg-color, #f5f5f5);
  color: var(--text-color, #333);
`;

/**
 * LazyVisualization - A component that lazily renders its children when they enter the viewport
 *
 * @param {React.ReactNode} children - The visualization component to render
 * @param {React.ReactNode} placeholder - Optional placeholder to show while loading
 * @param {string} rootMargin - IntersectionObserver rootMargin, default "200px"
 * @param {number} threshold - IntersectionObserver threshold, default 0.1
 * @param {Function} onVisibilityChange - Optional callback when visibility changes
 */
const LazyVisualization: React.FC<LazyVisualizationProps> = ({
  children,
  placeholder,
  rootMargin = "200px",
  threshold = 0.1,
  onVisibilityChange,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const currentRef = ref.current;
    if (!currentRef) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        const newIsVisible = entry.isIntersecting;

        if (newIsVisible && !hasLoaded) {
          setIsVisible(true);
          setHasLoaded(true);
          onVisibilityChange?.(true);
        } else if (onVisibilityChange && newIsVisible !== isVisible) {
          setIsVisible(newIsVisible);
          onVisibilityChange(newIsVisible);
        }
      },
      {
        root: null,
        rootMargin,
        threshold,
      }
    );

    observer.observe(currentRef);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [rootMargin, threshold, isVisible, hasLoaded, onVisibilityChange]);

  return (
    <>
      {hasLoaded ? (
        <>{children}</>
      ) : (
        <Wrapper ref={ref}>
          {placeholder || <Placeholder>Loading visualization...</Placeholder>}
        </Wrapper>
      )}
    </>
  );
};

export default LazyVisualization;
